import { IHttpClient } from "../../../lib/http";
import { isNotBlank } from "../../../lib/stringUtils";
import { Page } from "../../shared/api/Page";
import {
  ADDON_PAYMENTS_METHODS_FILTER_LABEL,
  ADYEN_PAYMENTS_METHODS_FILTER_LABEL,
  CHECKOUT_PNP_PAYMENTS_METHODS_FILTER_LABEL,
  FREEDOM_PAY_PAYMENTS_METHODS_FILTER_LABEL,
  NEMURU_PAYMENTS_METHODS_FILTER_LABEL,
  NO_SHOW_STATUS,
  PAYCOMET_PAYMENTS_METHODS_FILTER_LABEL,
  PAYMENT_TYPES_VALUES,
  STRIPE_PAYMENTS_METHODS_FILTER_LABEL,
} from "../../witadmin/Common/constants";
import { Reservation } from "../domain/Reservation";
import { ReservationDto } from "./Reservation.dto";
import { toReservation } from "./reservation.mapper";

export type Filters = Partial<Reservation> & {
  noShow?: "true" | "false";
  showInvalidPending: boolean;
  showOnlyCallCenterReservations: boolean;
};

type GetAllRequestBody = {
  page: number;
  size: number;
  propertyTickers: string[];
  filters: {
    [index: string]: string;
  };
};

type ExportRequestBody = {
  propertyTickers?: string[];
  filters: {
    [index: string]: string;
  };
};

type SendReservationByEmailRequestBody = {
  hotelTicker: string;
  roomStayId: string;
  type: string;
  emailDestinationType: string;
  ignoreDefaultRecipients: boolean;
  recipients: string[];
};

type CancelReservationRequestBody = {
  propertyTicker: string;
  reservationId: string;
  cancellationReason: string;
};

type ConfirmReservationRequestBody = {
  newReservationStatus: string;
  newPaymentStatus: string | undefined;
};

type NoShowRequestBody = {
  propertyTicker: string;
  reservationId: string;
  newValueNoShow: string;
};

const DATE_FILTERS = [
  "bookingDate",
  "checkin",
  "checkout",
  "lastModificationDate",
  "cancellationDate",
];

const NON_GENERIC_FILTERS = [...DATE_FILTERS, "paymentType"];

export class ReservationApi {
  constructor(private readonly httpClient: IHttpClient) {}

  async getAll(
    propertyTickers: string[],
    filterValues: Filters,
    { currentPage, pageSize }: { currentPage: number; pageSize: number }
  ): Promise<Page<Reservation>> {
    const data: GetAllRequestBody = {
      filters: buildFilters(filterValues),
      page: currentPage,
      size: pageSize,
      propertyTickers,
    };

    // TODO: remove this code after resilience project is working
    const showInvalidPendingReservations = filterValues.showInvalidPending
      ? `?showInvalidPending=true`
      : "";

    const page = await this.httpClient.post<GetAllRequestBody, Page<ReservationDto>>(
      `/api/properties/reservations/list${showInvalidPendingReservations}`,
      data
    );

    return {
      ...page,
      content: page.content.map(it => toReservation(it)),
    };
  }

  async getAllMultiProperty(
    propertyTickers: string[],
    filterValues: Filters,
    { currentPage, pageSize }: { currentPage: number; pageSize: number }
  ): Promise<Page<Reservation>> {
    const data: GetAllRequestBody = {
      filters: buildFilters(filterValues),
      page: currentPage,
      size: pageSize,
      propertyTickers,
    };

    const showInvalidPendingReservations = filterValues.showInvalidPending
      ? `?showInvalidPending=true`
      : "";

    const page = await this.httpClient.post<GetAllRequestBody, Page<ReservationDto>>(
      `/api/properties/reservations/aggregated/list${showInvalidPendingReservations}`,
      data
    );

    return {
      ...page,
      content: page.content.map(it => toReservation(it)),
    };
  }

  exportMasked(propertyTickers: string[], filterValues: Filters): Promise<string> {
    const data: ExportRequestBody = {
      propertyTickers,
      filters: buildFilters(filterValues),
    };

    // TODO: remove this code after resilience project is working
    const showInvalidPendingReservations = filterValues.showInvalidPending
      ? `?showInvalidPending=true`
      : "";

    return this.httpClient.post<ExportRequestBody, string>(
      `/api/properties/reservations/masked/list${showInvalidPendingReservations}`,
      data
    );
  }

  exportUnmasked(propertyTicker: string, filterValues: Filters): Promise<string> {
    const data: ExportRequestBody = {
      filters: buildFilters(filterValues),
      propertyTickers: undefined,
    };

    // TODO: remove this code after resilience project is working
    const showInvalidPendingReservations = filterValues.showInvalidPending
      ? `?showInvalidPending=true`
      : "";

    return this.httpClient.post<ExportRequestBody, string>(
      `/api/properties/${propertyTicker}/reservations/unmasked/send${showInvalidPendingReservations}`,
      data
    );
  }

  exportUnmaskedMultiProperty(propertyTickers: string[], filterValues: Filters): Promise<string> {
    const data: ExportRequestBody = {
      filters: buildFilters(filterValues),
      propertyTickers,
    };

    // TODO: remove this code after resilience project is working
    const showInvalidPendingReservations = filterValues.showInvalidPending
      ? `?showInvalidPending=true`
      : "";

    return this.httpClient.post<ExportRequestBody, string>(
      `/api/properties/reservations/unmasked/send${showInvalidPendingReservations}`,
      data
    );
  }

  async sendUnmaskedReservationByEmail(propertyTicker: string, reservationId: string) {
    return this.httpClient.post(
      `/api/properties/${propertyTicker}/reservations/${reservationId}/unmasked/send`,
      undefined
    );
  }

  async sendReservationByEmail(bookingId: string, propertyId: string, type: string): Promise<void> {
    const toSend = {
      hotelTicker: `${propertyId}`,
      roomStayId: `${bookingId}`,
      type: "RESERVATION_CONFIRMATION",
      emailDestinationType: `${type}`,
      ignoreDefaultRecipients: false,
      recipients: [],
    };
    await this.httpClient.post<SendReservationByEmailRequestBody, void>(
      `/api/emails/reservations`,
      toSend
    );
  }

  async cancelReservation(
    bookingId: string,
    propertyId: string,
    cancellationReason: string
  ): Promise<void> {
    const toSend = {
      propertyTicker: propertyId,
      reservationId: bookingId,
      cancellationReason: cancellationReason
        ? `Cancelled through backoffice: ${cancellationReason}`
        : "Cancelled through backoffice",
    };
    await this.httpClient.delete<CancelReservationRequestBody>(
      `/api/properties/${propertyId}/reservations`,
      { data: toSend }
    );
  }

  async confirmReservation(
    bookingId: string,
    propertyId: string,
    paymentType: string
  ): Promise<void> {
    const toSend = {
      newReservationStatus: "RESERVATION",
      newPaymentStatus:
        paymentType === PAYMENT_TYPES_VALUES.tr ||
        paymentType === PAYMENT_TYPES_VALUES.reservation_request
          ? "5"
          : undefined,
    };

    if (paymentType === PAYMENT_TYPES_VALUES.deferred) {
      await this.httpClient.put<ConfirmReservationRequestBody, void>(
        `/api/properties/${propertyId}/reservations/${bookingId}/deferred/force-confirm`,
        toSend
      );
    } else {
      await this.httpClient.patch<ConfirmReservationRequestBody, void>(
        `/api/properties/${propertyId}/reservations/${bookingId}`,
        toSend
      );
    }
  }

  async noShowReservation(
    bookingId: string,
    propertyTicker: string,
    noShowStatus: string
  ): Promise<void> {
    const toSend = {
      propertyTicker,
      reservationId: bookingId,
      newValueNoShow:
        noShowStatus === NO_SHOW_STATUS.NO_SHOW ? NO_SHOW_STATUS.SHOWED : NO_SHOW_STATUS.NO_SHOW,
    };
    await this.httpClient.put<NoShowRequestBody, void>(
      `/api/properties/${propertyTicker}/reservations/noshow`,
      toSend
    );
  }
}

const keyMapping = {
  bookingDate: "BookingDate",
  checkin: "CheckIn",
  checkout: "CheckOut",
  lastModificationDate: "LastModificationDate",
  mealPlanTicker: "board",
  configurationTicker: "accommodationConf",
  conditionTicker: "conditionName",
  inventoryTicker: "accommodation",
  promotions: "promoTickers",
  comments: "comments",
  internalNotes: "internalNotes",
  totalAmount: "amount",
  isCombined: "isCombined",
  promoCode: "promoCode",
  onlineStoreOrderId: "onlineStoreOrderId",
  cancellationDate: "CancellationDate",
  communicationConsent: "communicationConsent",
};

function buildFilters(filterValues: Filters): { [index: string]: string } {
  let filters: { [index: string]: string } = {};
  if (filterValues.showOnlyCallCenterReservations) {
    filters.origin = "callcenter";
  }

  if (filterValues.noShow) {
    filters.noShow =
      filterValues.noShow === "true" ? NO_SHOW_STATUS.NO_SHOW : NO_SHOW_STATUS.SHOWED;
  }

  filters = { ...filters, ...buildDateFilters(filterValues) };
  filters = { ...filters, ...buildPaymentMethodFilters(filterValues) };

  // other filters
  Object.entries(filterValues).forEach(([key, value]) => {
    const mappedKey: string = keyMapping[key];
    if (value && value !== "undefined" && !NON_GENERIC_FILTERS.includes(key)) {
      filters[mappedKey || key] = value as string;
    }
  });

  return filters;
}

function buildDateFilters(filterValues: Filters): { [key: string]: string } {
  return Object.keys(filterValues)
    .filter(key => DATE_FILTERS.includes(key))
    .reduce((filter, key) => {
      const mappedKey: string = keyMapping[key];

      if (isNotBlank(filterValues[key][`startDate`])) {
        filter[`start${mappedKey}`] = filterValues[key][`startDate`].slice(
          0,
          filterValues[key][`startDate`].indexOf("T")
        );
      }
      if (isNotBlank(filterValues[key][`endDate`])) {
        filter[`end${mappedKey}`] = filterValues[key][`endDate`].slice(
          0,
          filterValues[key][`endDate`].indexOf("T")
        );
      }
      return filter;
    }, {});
}

function buildPaymentMethodFilters(
  filterValues: Filters
): { paymentType: string; paymentMethod: string } | {} {
  const paymentType = filterValues["paymentType"];

  /**
   * PayNoPain checkout payment type filter
   */
  if (paymentType?.startsWith("pnp_")) {
    const paymentMethod = getPaymentMethod(paymentType, CHECKOUT_PNP_PAYMENTS_METHODS_FILTER_LABEL);
    if (paymentMethod) {
      return {
        paymentType: PAYMENT_TYPES_VALUES.checkout_pnp,
        paymentMethod,
      };
    }
  }

  /**
   * Paycomet checkout payment type filter
   */
  if (paymentType?.startsWith("pcomet_")) {
    const paymentMethod = getPaymentMethod(paymentType, PAYCOMET_PAYMENTS_METHODS_FILTER_LABEL);
    if (paymentMethod) {
      return {
        paymentType: PAYMENT_TYPES_VALUES.paycomet,
        paymentMethod,
      };
    }
  }

  /**
   * Addon payments checkout payment type filter
   */
  if (paymentType?.startsWith("addon_pay_")) {
    const paymentMethod = getPaymentMethod(paymentType, ADDON_PAYMENTS_METHODS_FILTER_LABEL);
    if (paymentMethod) {
      return {
        paymentType: PAYMENT_TYPES_VALUES.addon_payments,
        paymentMethod,
      };
    }
  }

  /**
   * Adyen checkout payment type filter
   */
  if (paymentType?.startsWith("adyen_")) {
    const paymentMethod = getPaymentMethod(paymentType, ADYEN_PAYMENTS_METHODS_FILTER_LABEL);
    if (paymentMethod) {
      return {
        paymentType: PAYMENT_TYPES_VALUES.adyen,
        paymentMethod,
      };
    }
  }

  /**
   * Stripe checkout payment type filter
   */
  if (paymentType?.startsWith("stripe_")) {
    const paymentMethod = getPaymentMethod(paymentType, STRIPE_PAYMENTS_METHODS_FILTER_LABEL);
    if (paymentMethod) {
      return {
        paymentType: PAYMENT_TYPES_VALUES.stripe,
        paymentMethod,
      };
    }
  }

  /**
   * Freedom pay checkout payment type filter
   */
  if (paymentType?.startsWith("freedompay_")) {
    const paymentMethod = getPaymentMethod(paymentType, FREEDOM_PAY_PAYMENTS_METHODS_FILTER_LABEL);
    if (paymentMethod) {
      return {
        paymentType: PAYMENT_TYPES_VALUES.freedom_pay,
        paymentMethod,
      };
    }
  }

  /**
   * Nemuru checkout payment type filter
   */
  if (paymentType?.startsWith("nemuru_")) {
    const paymentMethod = getPaymentMethod(paymentType, NEMURU_PAYMENTS_METHODS_FILTER_LABEL);
    if (paymentMethod) {
      return {
        paymentType: PAYMENT_TYPES_VALUES.nemuru,
        paymentMethod,
      };
    }
  }

  return { paymentType };
}

function getPaymentMethod(paymentMethod: string, paymentMethods: { [key: string]: string }) {
  const index = Object.values(paymentMethods).indexOf(paymentMethod);
  return Object.keys(paymentMethods)[index];
}
